<template>
    <div class="row">
        <div class="col-12 mb-4 d-flex flex-row align-items-center">
            <h1 class="mr-auto">{{ $t('background-event') }}</h1>
            <button 
                :disabled="loading || submitting" 
                type="button" 
                class="btn btn-primary" 
                @click="saveBackgroundEvent"
            >
                {{ id ? $t('save') : $t('create') }}
            </button>
            <button
                v-if="id"
                :disabled="loading || submitting" 
                type="button" 
                class="btn btn-outline-danger ml-2" 
                @click="showDeleteDialog"
            >
                {{ $t('delete') }}
            </button>
        </div>

        <template v-if="!loading">
            <div v-if="saved" class="col-12 mb-2">
                <div class="alert alert-success">{{ $t('bg-event-saved') }}</div>
            </div>
            <!-- Details -->
            <div class="col-12 mb-4">
                <div class="list-group border-radius-2 no-border">
                    <div class="list-group-item d-flex flex-column flex-lg-row">
                        <TextInput v-model="bgEvent.title" :label="$t('title')" class="flex-grow-1 mr-lg-2"/>
                        <DateInput v-model="bgEvent.datefrom" :label="$t('from')" class="flex-grow-1 mr-lg-2"/>
                        <DateInput v-model="bgEvent.dateto" :label="$t('until')" class="flex-grow-1"/>
                    </div>
                </div>
            </div>

            <!-- Groups -->
            <div class="col-12 mb-4">
                <div class="list-group border-radius-2 no-border">
                    <div class="list-group-item">
                        <GroupMultiSelect v-model="bgEvent.groups" :groups="timetable.groups" />
                    </div>
                </div>
            </div>

        </template>

        <div v-else class="col-12">
            <div class="skeleton midnight h20 w100 mr-4 mb-3 float-left" v-for="n in 24" :key="n" />
        </div>
    </div>
</template>

<script>
import DateInput from "src/components/Inputs/DateInput.vue"
import TextInput from "src/components/Inputs/TextInput.vue"
import GroupMultiSelect from "src/components/ProgrammeYearGroups/GroupMultiSelect.vue"

export default {
    components: {
        DateInput,
        TextInput,
        GroupMultiSelect,
    },
    mounted() {
        Promise.all([this.fetchBackgroundEvent(), this.fetchTimetable()]).then(() => {
            this.loading = false
        })
    },
    data() {
        return {
            loading: true,
            submitting: false,
            saved: false,
            id: Number(this.$route.params.eventId),
            bgEvent: {
                tttimetable_id: this.$route.params.timetableId,
                title: '',
                datefrom: '',
                dateto: '',
                groups: [],
            },
            timetable: null,
        }
    },
    methods: {
        fetchBackgroundEvent() {
            if (!this.id) return Promise.resolve()

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/backgroundevents/get`, {
                method: 'GET',
                data: { id: this.id },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error()

                this.bgEvent = {
                    ...res.result,
                    datefrom: this.$datetime.getDateFieldValueFromTimestamp(res.result.datefrom),
                    dateto: this.$datetime.getDateFieldValueFromTimestamp(res.result.dateto),
                }
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-bg-event'),
                    // confirmCallBack: () => {
                    //     this.$router.push(`/timetables/edit/${this.$route.params.timetableId}`)
                    // }
                })
            })
        },
        fetchTimetable() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/get`, {
                method: 'GET',
                data: {
                    id: this.$route.params.timetableId,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error()

                this.timetable = res.result
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-timetable'),
                    confirmCallBack: () => { this.$router.push(`/timetables`) }
                })
            })
        },
        saveBackgroundEvent() {
            if (this.submitting) return

            this.submitting = true
            this.saved = false
            this.$store.commit('showBlockingSpinner')

            this.$cfetch(`${this.$settings.APIBaseURL}/timetables/backgroundevents/save`, {
                method: 'POST',
                data: {
                    ...this.bgEvent,
                    id: this.id ? this.id : undefined,
                    groups: this.bgEvent.groups.map(gr => {
                        return {
                            ...gr,
                            id: gr.group_id
                        }
                    })
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error()

                this.saved = true
                if (!this.id) this.$router.push(`/timetables/edit/${this.timetable.id}/backgroundevents`)
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-saving-bg-event'),
                    confirmCallBack: () => { this.$router.push(`/timetables`) }
                })
            }).finally(() => {
                this.submitting = false
                this.$store.commit('hideBlockingSpinner')
            })
        },
        showDeleteDialog() {
            this.$store.commit('openDialog', {
                title: this.$t('are-you-sure'),
                content: this.$t('are-you-sure-delete-event'),
                cancelBtn: true,
                confirmCallBack: this.deleteBackgroundEvent,
                confirmBtnText: this.$t('delete'),
            })
        },
        deleteBackgroundEvent() {
            if (this.submitting) return
            this.submitting = true

            this.$store.commit('showBlockingSpinner')

            this.$cfetch(`${this.$settings.APIBaseURL}/timetables/backgroundevents/delete`, {
                method: 'DELETE',
                data: { id: this.id },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error()

                this.$router.push(`/timetables/edit/${this.timetable.id}`)
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-deleting-bg-event'),
                })
            }).finally(() => {
                this.submitting = false
                this.$store.commit('hideBlockingSpinner')
            })
        },
    }
}
</script>